import React, { useMemo, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { MapPin, Calendar, HandHeart, Filter } from 'lucide-react';
import { mapPins } from '../mock';

// Black Country centre (roughly between Wolverhampton, Walsall, Dudley, Sandwell)
const CENTER = [52.545, -2.05];
const ZOOM = 11;

const makePin = (color) =>
  L.divIcon({
    className: 'acci-map-pin',
    html: `
      <div style="
        position: relative;
        width: 34px;
        height: 44px;
        transform: translate(-50%, -100%);
      ">
        <div style="
          position: absolute; left: 50%; top: 0; transform: translateX(-50%);
          width: 32px; height: 32px; border-radius: 999px;
          background: ${color};
          border: 3px solid #ffffff;
          box-shadow: 0 6px 14px rgba(14,17,18,0.25);
        "></div>
        <div style="
          position: absolute; left: 50%; top: 26px; transform: translateX(-50%) rotate(45deg);
          width: 12px; height: 12px; background: ${color};
          border-right: 3px solid #ffffff;
          border-bottom: 3px solid #ffffff;
        "></div>
      </div>
    `,
    iconSize: [34, 44],
    iconAnchor: [17, 44],
    popupAnchor: [0, -40],
  });

const iconEvent = makePin('#6e12ed');
const iconVolunteer = makePin('#79c4c7');

const FILTERS = [
  { id: 'all', label: 'All', icon: Filter },
  { id: 'event', label: 'Events', icon: Calendar },
  { id: 'volunteer', label: 'Volunteer', icon: HandHeart },
];

const CommunityMap = () => {
  const [filter, setFilter] = useState('all');

  const pins = useMemo(
    () => (filter === 'all' ? mapPins : mapPins.filter((p) => p.type === filter)),
    [filter]
  );

  const counts = useMemo(
    () => ({
      event: mapPins.filter((p) => p.type === 'event').length,
      volunteer: mapPins.filter((p) => p.type === 'volunteer').length,
    }),
    []
  );

  return (
    <section
      id="community-map"
      data-testid="community-map-section"
      className="relative py-24 lg:py-28 bg-white overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-10">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-semibold text-neutral-500 mb-4">
              <MapPin className="w-3.5 h-3.5" />
              Community Map
            </div>
            <h2 className="font-serif text-4xl lg:text-6xl font-bold text-[#0E1112] leading-[1.02]">
              Take a stand,<br />
              in your <span className="hl-box hl-purple">postcode</span>.
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-neutral-700 text-lg leading-relaxed">
              Explore live events and volunteer opportunities across the Black Country — from
              Wolverhampton to Walsall, Dudley and Sandwell. Drop a pin, show up, take a stand.
            </p>
          </div>
        </div>

        {/* Filters + legend */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-5">
          <div
            data-testid="community-map-filters"
            className="inline-flex items-center bg-neutral-100 rounded-full p-1"
          >
            {FILTERS.map((f) => {
              const Icon = f.icon;
              const active = filter === f.id;
              return (
                <button
                  key={f.id}
                  data-testid={`map-filter-${f.id}`}
                  onClick={() => setFilter(f.id)}
                  className={`inline-flex items-center gap-2 px-4 h-10 rounded-full text-sm font-semibold transition-colors ${
                    active
                      ? 'bg-[#0E1112] text-white'
                      : 'text-neutral-700 hover:text-[#0E1112]'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {f.label}
                </button>
              );
            })}
          </div>

          <div className="flex items-center gap-5 text-xs text-neutral-600">
            <span className="inline-flex items-center gap-2">
              <span className="inline-block w-3 h-3 rounded-full bg-[#6e12ed]" />
              {counts.event} events
            </span>
            <span className="inline-flex items-center gap-2">
              <span className="inline-block w-3 h-3 rounded-full bg-[#79c4c7]" />
              {counts.volunteer} volunteer roles
            </span>
          </div>
        </div>

        {/* Map + list */}
        <div className="grid lg:grid-cols-12 gap-6">
          <div
            data-testid="community-map-canvas"
            className="lg:col-span-8 rounded-3xl overflow-hidden border border-black/10 shadow-sm"
            style={{ height: '520px' }}
          >
            <MapContainer
              center={CENTER}
              zoom={ZOOM}
              scrollWheelZoom={false}
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {pins.map((p) => (
                <Marker
                  key={p.id}
                  position={[p.lat, p.lng]}
                  icon={p.type === 'event' ? iconEvent : iconVolunteer}
                >
                  <Popup>
                    <div style={{ minWidth: '180px' }}>
                      <div
                        style={{
                          textTransform: 'uppercase',
                          fontSize: '10px',
                          letterSpacing: '0.15em',
                          fontWeight: 700,
                          color: p.type === 'event' ? '#6e12ed' : '#79c4c7',
                          marginBottom: '4px',
                        }}
                      >
                        {p.type === 'event' ? 'Event' : 'Volunteer'}
                      </div>
                      <div style={{ fontWeight: 700, fontSize: '15px', color: '#0E1112' }}>
                        {p.title}
                      </div>
                      <div style={{ fontSize: '12px', color: '#525252', marginTop: '4px' }}>
                        {p.detail}
                      </div>
                      <div style={{ fontSize: '12px', color: '#404040', marginTop: '6px' }}>
                        {p.body}
                      </div>
                      <div
                        style={{
                          fontSize: '11px',
                          color: '#737373',
                          marginTop: '8px',
                          borderTop: '1px solid #f0f0f0',
                          paddingTop: '6px',
                        }}
                      >
                        {p.address}
                      </div>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>

          <div className="lg:col-span-4 space-y-3 max-h-[520px] overflow-y-auto pr-1 no-scrollbar">
            {pins.map((p) => (
              <div
                key={p.id}
                data-testid={`map-pin-card-${p.id}`}
                className="group rounded-2xl border border-black/10 bg-white p-5 hover:border-[#6e12ed] hover:shadow-md transition-all"
              >
                <div className="flex items-start justify-between gap-3">
                  <div
                    className="text-[10px] uppercase tracking-widest font-bold"
                    style={{ color: p.type === 'event' ? '#6e12ed' : '#79c4c7' }}
                  >
                    {p.type === 'event' ? 'Event' : 'Volunteer'}
                  </div>
                  <span
                    className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: p.type === 'event' ? '#6e12ed' : '#79c4c7' }}
                  />
                </div>
                <h3 className="font-serif text-lg font-bold text-[#0E1112] mt-1 leading-snug">
                  {p.title}
                </h3>
                <div className="text-xs text-neutral-600 mt-1">{p.detail}</div>
                <div className="text-xs text-neutral-500 mt-2 flex items-start gap-1">
                  <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" />
                  {p.address}
                </div>
              </div>
            ))}

            {pins.length === 0 && (
              <div className="rounded-2xl border border-dashed border-black/15 p-6 text-sm text-neutral-500 text-center">
                Nothing here yet — try another filter.
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CommunityMap;
